import styles from '@/components/agents/agents.module.css';

import clsxm from '@/lib/clsxm';

import {
  CheckGlyph,
  delay,
  Stage,
  tone,
  useCopyGap,
} from '@/components/agents/primitives';
import LandingPageNavigation from '@/components/layout/LandingPageNavigation';

import { AUDIOS_IN_PLAN } from '@/constants/common';

import { useLandingT, usePageVars } from './kit';
import LandingDropzone from './LandingDropzone';
import LandingRecorder from './LandingRecorder';
import type { LandingCopy } from './pages';
import TranscriptMock from './TranscriptMock';

/**
 * Headline and upload box first (people arrive here to transcribe a file),
 * then a still of the transcript page for the same kind of recording.
 */
export default function TranscriptionHero({ page }: { page: LandingCopy }) {
  const { t } = useLandingT();
  const gap = useCopyGap();
  const vars = usePageVars(page);

  const points = [
    t('Speaker labels and timestamps'),
    t('Summaries, notes and subtitles'),
    t('45 languages, detected for you'),
  ];

  return (
    <header className='relative'>
      <LandingPageNavigation />

      <div className='mx-auto max-w-7xl px-6 pt-10 sm:pt-16 lg:px-8'>
        <div className='grid gap-x-14 gap-y-10 lg:grid-cols-12 lg:items-center'>
          <div className='lg:col-span-6 xl:col-span-7'>
            <p
              className={clsxm(
                styles.enter,
                'inline-flex items-center gap-2 rounded-full bg-white py-1 pe-3.5 ps-1 text-sm font-medium shadow-[0_1px_2px_rgba(15,15,40,0.05)] ring-1 ring-[#0f0f28]/[0.08] dark:bg-white/[0.05] dark:ring-white/10',
                tone.body,
              )}
            >
              <span className='flex-none whitespace-nowrap rounded-full bg-primary-600 px-2 py-0.5 text-xs font-semibold text-white'>
                {t('Free to try')}
              </span>
              {t(page.eyebrow, vars)}
            </p>

            <h1
              className={clsxm(
                styles.display,
                styles.heroTitle,
                styles.enter,
                'mt-6 text-[2.625rem] font-semibold leading-[1.04] sm:text-6xl sm:leading-[1.02] xl:text-[4.25rem]',
                tone.ink,
              )}
              style={delay(60)}
            >
              {t(page.title, vars)}
              {page.titleTail ? (
                <>
                  {gap}
                  <span className='text-primary-600 dark:text-primary-400'>
                    {t(page.titleTail, vars)}
                  </span>
                </>
              ) : null}
            </h1>

            <p
              className={clsxm(
                styles.enter,
                'mt-6 max-w-xl text-lg leading-8 sm:text-[1.25rem] sm:leading-[1.6]',
                tone.body,
              )}
              style={delay(140)}
            >
              {t(page.lead, vars)}
            </p>

            <ul
              className={clsxm(
                styles.enter,
                'mt-7 flex flex-wrap gap-x-5 gap-y-2 text-sm font-medium',
                tone.body,
              )}
              style={delay(200)}
            >
              {points.map((point) => (
                <li key={point} className='flex items-center gap-2'>
                  <span className='flex h-5 w-5 items-center justify-center rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'>
                    <CheckGlyph className='h-3 w-3' />
                  </span>
                  {point}
                </li>
              ))}
            </ul>
          </div>

          <div
            className={clsxm(styles.enter, 'lg:col-span-6 xl:col-span-5')}
            style={delay(220)}
          >
            {page.cta === 'record' ? (
              <LandingRecorder page={page.id} />
            ) : (
              <LandingDropzone page={page.id} link={page.link} />
            )}
            <p className={clsxm('mt-4 text-center text-[13px]', tone.quiet)}>
              {t(
                '{{count}} free transcriptions every month. No credit card needed.',
                { count: AUDIOS_IN_PLAN.Free },
              )}
            </p>
          </div>
        </div>
      </div>

      <div className='mx-auto mt-14 max-w-7xl px-3 sm:mt-20 sm:px-6 lg:px-8'>
        <Stage
          variant='indigo'
          halftoneAt='100% 100%'
          className={clsxm(
            styles.enter,
            'px-4 py-8 sm:px-10 sm:py-12 lg:px-12 lg:py-14',
          )}
          style={delay(300)}
        >
          <p className='sr-only'>
            {t(
              'Example: a transcript with speaker names and timestamps, and AI notes written from it.',
            )}
          </p>
          <TranscriptMock example={page.example} start={420} />
        </Stage>
      </div>
    </header>
  );
}
